'use client';

import {useState, useEffect} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {ChevronDown, ChevronRight} from 'lucide-react';
import {cn} from '@/matic/lib/utils/cn';
import {type NavItemConfig} from './navConfig';

type NavItemProps = {
  item: NavItemConfig;
  level?: number;
};

export default function NavItem({item, level = 0}: NavItemProps) {
  const location = useLocation();
  const {pathname, search} = location;
  const hasChildren = Boolean(item.children && item.children.length > 0);

  // Auto-expand if any child route is active
  const hasActiveChild = hasChildren
    ? item.children!.some((child) => {
        if (!child.path) return false;
        if (child.path.includes('?')) return pathname + search === child.path;
        return pathname === child.path || pathname.startsWith(child.path + '/');
      })
    : false;

  // Determine active state, accounting for query parameters in item.path
  const isActive = (() => {
    if (!item.path) return false;

    const hasQuery = item.path.includes('?');
    if (hasQuery) {
      // For paths with query params (e.g. /records?templateId=xxx),
      // match both pathname and the specific query param
      return pathname + search === item.path;
    }

    // Exact pathname match — active regardless of query params
    if (pathname === item.path) {
      return true;
    }

    // Prefix match: item.path is a parent of the current path (e.g.,
    // /records/types matches /records/types/abc). Only match when the
    // remaining segment looks like an ID (starts with a digit), not a
    // named sub-route (starts with a letter). Also skip when a sibling
    // nav item with query params is a more specific match.
    if (
      pathname.startsWith(item.path + '/') &&
      !/^\/[a-z]/i.test(pathname.slice(item.path.length))
    ) {
      // If a sibling child item matches this exact pathname, defer to it
      return search === '' || search === '?';
    }

    return false;
  })();

  // Auto-expand when this row or any of its children is active so that
  // pinned items are visible whenever the user is in this feature area.
  const shouldAutoExpand = hasChildren && (hasActiveChild || isActive);

  const [isOpen, setIsOpen] = useState(item.isOpen ?? shouldAutoExpand);

  useEffect(() => {
    if (shouldAutoExpand && !isOpen) {
      setIsOpen(true);
    }
  }, [shouldAutoExpand]);

  const Icon = item.icon;

  const labelContent = (
    <div className="flex flex-1 items-center">
      {Icon && (
        <Icon
          className={cn(
            'mr-3 h-4 w-4 shrink-0',
            isActive && 'text-primary-500'
          )}
        />
      )}
      <span className="text-sm font-medium">{item.label}</span>
      {item.badge && (
        <span className="ml-4 flex h-5 w-5 items-center justify-center rounded-full border border-primary-500 bg-transparent text-[10px] font-bold text-primary-500">
          {item.badge}
        </span>
      )}
    </div>
  );

  // Active bar is a wrapper-level sibling so its absolute positioning
  // resolves to the row container, never to an inner Link/button.
  const activeBar = isActive ? (
    <div
      className={cn(
        'absolute bottom-0 top-0 w-1 bg-primary-500',
        level > 0 ? 'left-6' : 'left-0'
      )}
    />
  ) : null;

  const chevronIcon = isOpen ? (
    <ChevronDown className="h-4 w-4" />
  ) : (
    <ChevronRight className="h-4 w-4" />
  );

  const rowBaseClass = cn(
    'relative flex w-full cursor-pointer items-center text-left transition-colors',
    isActive
      ? 'text-primary-500'
      : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-white'
  );

  const leftPadding = level > 0 ? 'pl-12' : 'pl-6';

  const focusRing =
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500';

  const toggleAriaLabel = `${isOpen ? 'Collapse' : 'Expand'} ${item.label.toLowerCase()}`;

  // Case 1: link-only leaf
  if (item.path && !hasChildren) {
    return (
      <Link
        to={item.path}
        className={cn(
          rowBaseClass,
          leftPadding,
          'py-2 pr-4 no-underline',
          focusRing
        )}
      >
        {activeBar}
        {labelContent}
      </Link>
    );
  }

  // Case 2: link + children — label area navigates, chevron toggles
  if (item.path && hasChildren) {
    return (
      <>
        <div className={rowBaseClass}>
          {activeBar}
          <Link
            to={item.path}
            className={cn(
              'flex flex-1 items-center py-2 pr-2 text-inherit no-underline',
              leftPadding,
              focusRing
            )}
          >
            {labelContent}
          </Link>
          <button
            type="button"
            aria-label={toggleAriaLabel}
            onClick={() => {
              setIsOpen((current) => !current);
            }}
            className={cn(
              'flex shrink-0 items-center self-stretch px-4 text-gray-400 hover:bg-sidebar-active hover:text-white',
              focusRing
            )}
          >
            {chevronIcon}
          </button>
        </div>

        {isOpen && (
          <div className="flex flex-col">
            {item.children!.map((child) => (
              <NavItem key={child.label} item={child} level={level + 1} />
            ))}
          </div>
        )}
      </>
    );
  }

  // Case 3: no path — button toggles dropdown (or inert if no children)
  return (
    <>
      <button
        type="button"
        aria-label={hasChildren ? toggleAriaLabel : undefined}
        className={cn(rowBaseClass, leftPadding, 'py-2 pr-4', focusRing)}
        onClick={() => {
          if (hasChildren) {
            setIsOpen((current) => !current);
          }
        }}
      >
        {activeBar}
        {labelContent}
        {hasChildren && <div className="text-gray-400">{chevronIcon}</div>}
      </button>

      {hasChildren && isOpen && (
        <div className="flex flex-col">
          {item.children!.map((child) => (
            <NavItem key={child.label} item={child} level={level + 1} />
          ))}
        </div>
      )}
    </>
  );
}
